import React, { useEffect, useState } from 'react';
import { Card, Container } from 'react-bootstrap';
import TestScriptsFormModal from './TestScriptsFormModal';
import { Add, Attachment, Delete, Edit } from '@mui/icons-material';
import {
  testStepsTableHeaders,
  uriField,
} from '../../../test-case/testCaseTableHead';
import DraggableTableComponent from '../../../../elements/DraggableTableComponent';
import {
  changeStepNumber,
  cloneTestSteps,
  deleteTestStep,
  getAllTestSteps,
} from '../../../../../modules/teststep';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useNotification } from '../../../../../hooks/NotificationContext';
import { Button } from '@mui/material';
import TestScriptsAttachments from './TestScriptsAttachments';
import ConfirmationModal from '../../../../elements/ConfirmationModal';
import CustomIconButton from '../../../../elements/CustomIconButton';
import { isExecutiveManager } from '../../../../../lib/userUtils';

const TestScriptsContainer = ({ data, testCaseId }) => {
  const initialScript = { step: '', testData: '', expectedResult: '' };
  const [order, setOrder] = useState('asc');
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(false);
  const [selectedScript, setSelectedScript] = useState(initialScript);
  const [mode, setMode] = useState('add');
  const [formData, setFormData] = useState({ scripts: [] });
  const { addNotification } = useNotification();
  const [openAttachments, setOpenAttachments] = useState(false);
  const [totalScripts, setTotalScripts] = useState(0);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const technology_layer = data && data.technology_layer;
  const handleClose = () => {
    setShow(false);
    setSelectedScript(initialScript);
  };

  const fetchScripts = async () => {
    try {
      const response = await getAllTestSteps(testCaseId);
      if (response && response.rows && Array.isArray(response.rows)) {
        setTotalScripts(response?.rows?.length);
        const sortedScripts = response.rows
          .map((script) => ({
            testData: script.test_data,
            expectedResult: script.expected_result,
            step: script.step,
            attachments: script.attachments,
            id: script.id,
            uri: script.uri,
            is_cloned: script.is_cloned,
            sNo: script.s_no,
          }))
          .sort((a, b) => a.sNo - b.s_no);
        setFormData({ scripts: sortedScripts });
      }
    } catch (error) {
      const errorMessage = 'Failed to fetch test steps.';
      addNotification('danger', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const updateSequence = async (steps) => {
    try {
      await changeStepNumber(steps);
      await fetchScripts();
    } catch (error) {
      const errorMessage = 'Failed to update test steps.';
      addNotification('danger', errorMessage);
    }
  };

  useEffect(() => {
    setLoading(true);
    if (data?.id) fetchScripts();
  }, [data?.id]);

  const handleEditClick = (row) => {
    setMode('edit');
    setSelectedScript(row);
    setShow(true);
  };

  const handleAddAttachment = (row) => {
    setSelectedScript(row);
    setOpenAttachments(true);
  };

  const handleAddScript = () => {
    setMode('add');
    setSelectedScript(initialScript);
    setShow(true);
  };

  const handleCloneScript = async (row) => {
    const { created_at, updated_at, attachments, ...rest } = row;
    try {
      await cloneTestSteps(
        { testStep: { ...rest, isCloning: row?.id } },
        testCaseId
      );
      addNotification('success', 'Test step cloned successfully!');
      fetchScripts();
    } catch (error) {
      const errorMessage =
        error?.message || 'Failed to clone test step. Please try again.';
      addNotification('danger', errorMessage);
    }
  };

  const handleDeleteScript = async () => {
    try {
      await deleteTestStep(selectedScript?.id);
      addNotification('success', 'Test step deleted successfully!');
      handleConfirmClose();
      fetchScripts();
    } catch (error) {
      const errorMessage =
        error?.message || 'Failed to delete test step. Please try again.';
      addNotification('danger', errorMessage);
    }
  };

  const handleOpenConfirmation = (row) => {
    setSelectedScript(row);
    setOpenConfirmation(true);
  };

  const handleConfirmClose = () => {
    setSelectedScript(null);
    setOpenConfirmation(false);
  };

  const ActionButton = ({ row }) => (
    <div className="d-flex justify-content-end">
      {!isExecutiveManager() ? (
        <>
          <CustomIconButton
            size="sm"
            title="Edit"
            onClick={() => handleEditClick(row)}>
            <Edit />
          </CustomIconButton>
          <CustomIconButton
            size="sm"
            title="Clone"
            onClick={() => handleCloneScript(row)}>
            <ContentCopyIcon />
          </CustomIconButton>
          <CustomIconButton
            size="sm"
            title="Delete"
            onClick={() => handleOpenConfirmation(row)}>
            <Delete sx={{ color: 'red' }} />
          </CustomIconButton>
        </>
      ) : null}

      <CustomIconButton
        size="sm"
        title={`Add${row?.attachments.length > 0 ? '/View' : ''} Attachments`}
        sx={{ color: row?.attachments.length > 0 ? '#1b3f87' : 'grey' }}
        onClick={() => handleAddAttachment(row)}>
        <Attachment />
      </CustomIconButton>
    </div>
  );
  const uriHeader =
    technology_layer && technology_layer === 'API/Integration' ? uriField : [];
  return (
    <Container fluid className="px-0">
      <Card className="shadow-sm mb-4">
        <Card.Header
          className="d-flex justify-content-between align-items-center"
          style={{ height: '50px' }}>
          <h5 className="mb-0">Test Steps</h5>
          {!isExecutiveManager() && (
            <Button
              variant="contained"
              onClick={handleAddScript}
              disabled={data?.is_archived}>
              <Add color={'#fff'} /> Add Test Steps
            </Button>
          )}
        </Card.Header>
        <Card.Body>
          <DraggableTableComponent
            data={formData.scripts}
            headerTabs={[...testStepsTableHeaders, ...uriHeader]}
            isLoading={loading}
            showCheckbox={false}
            striped
            order={order}
            updateSequence={updateSequence}
            setOrder={setOrder}
            action={(row, index) => <ActionButton row={row} index={index} />}
          />
          <TestScriptsFormModal
            show={show}
            handleClose={handleClose}
            script={selectedScript}
            fetchScripts={fetchScripts}
            testCaseId={data?.id}
            addNotification={addNotification}
            mode={mode}
            totalScripts={totalScripts}
            technology_layer={technology_layer}
          />
        </Card.Body>
      </Card>
      <TestScriptsAttachments
        show={openAttachments}
        handleClose={() => setOpenAttachments(false)}
        testStepId={selectedScript?.id}
        selectedScript={selectedScript}
        fetchScripts={fetchScripts}
      />
      <ConfirmationModal
        show={openConfirmation}
        handleConfirm={handleDeleteScript}
        handleClose={handleConfirmClose}
        title="Remove Test Step"
        body={
          <>
            Are you sure you want to remove{' '}
            <strong>step {selectedScript?.sNo}</strong>? Removing the test step will also remove any attachments or execution results associated with it.
          </>
        }
        buttonLabel="Yes"
        buttonLabelSecondary="No"
      />
    </Container>
  );
};

export default TestScriptsContainer;
